// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@eisinc/XDL/types";

import { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import ChevronDown from "@eisinc/XDL/Icons/ChevronDown";
import button from "@eisinc/XDL/PreStyle/button";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, styledScrollbar } from "@eisinc/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        buttonProps: {
            ...button.primary,
        },
        cssOverrides: {
            pagination: css`
                display: flex;
                gap: 32px;

                & {
                    margin: 0;
                    flex-wrap: nowrap;
                }
                > form,
                > nav {
                    width: auto;
                }
            `,
            linkList: css`
                & {
                    margin: 0;
                }
                button {
                    min-height: 24px;
                    min-width: 24px;
                    padding: 0 8px;
                    text-shadow: none;
                    width: auto;
                }

                ul {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 12px;
                }

                li {
                    &:first-child,
                    &:last-child {
                        width: 24px;
                        height: 24px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        button {
                            background-color: ${color("common.ui2")};
                            border-radius: 100%;
                            width: 24px;
                            height: 24px;
                            line-height: 16px;
                            margin: 0;
                            display: flex;
                            position: relative;
                            &:hover:not(:disabled) {
                                background-color: ${color("common.ui8")};
                                border-color: ${color("common.ui8")};

                                svg {
                                    color: ${color("common.white")};
                                }
                            }
                            &:focus:not(:disabled) {
                                background-color: ${color("common.ui9")};
                                border-color: ${color("common.ui9")};

                                svg {
                                    color: ${color("common.white")};
                                }
                            }
                            &:disabled {
                                border-color: ${color("primary.main", 0.33)};
                                opacity: 0.33;
                            }
                            span {
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                position: absolute;
                            }
                            svg {
                                color: ${color("primary.main")};
                            }
                        }
                    }
                }
            `,
            perPageSelect: css`
                & {
                    margin: 0;
                }
            `,
        },
        selectProps: {
            sizeVariant: "small",
            cssOverrides: {
                formField: css`
                    display: flex;
                    align-items: center;
                    select {
                        text-align: center;
                    }
                `,
                formInputWrapper: css`
                    width: 47px;
                `,
            },
            labelProps: {
                ...fontConfig.singleLineSmall,
                css: css`
                    width: auto;
                    padding-top: 0;
                    padding-right: 8px;
                    font-weight: normal;
                    min-height: unset;
                `,
            },
        },
        triggerLinkText: {
            ...fontConfig.singleLineSmall,
            css: css`
                margin-right: 4px;
            `,
        },
        triggerLinkIcon: { src: ChevronDown, color: "common.ui4", size: 10 },
        tooltip: {
            extendedStyles: {
                tooltipContent: {
                    css: css`
                        min-width: 110px;
                        padding-block: 10px;
                        padding-inline: 0;
                        ${StyledButton} {
                            height: 30px;
                            padding: 0 8px;
                            width: 100%;
                            &.current-page {
                                background: ${color("common.ui2")}
                            }
                        }
                        .tooltipInfo {
                            max-height: 238px;
                            ${styledScrollbar()}
                        }
                    `,
                },
            },
        },
    } as PaginationPresentationProps,
} as xBaseTheme["pagination"];
